import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from './client';
import { mapExpenseDoc, toTimestamp } from './mappers';
import type { Expense, ExpenseStatus, LineItem } from '@/lib/types';

export interface ExpenseDraft {
  user_id: string;
  transaction_date: string;
  store_name: string | null;
  items: LineItem[];
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  status: ExpenseStatus;
  notes: string | null;
  receipt_url: string | null;
}

export interface ExpenseRepository {
  listExpenses: () => Promise<Expense[]>;
  getExpenseById: (expenseId: string) => Promise<Expense | null>;
  createExpense: (data: ExpenseDraft) => Promise<string>;
  updateExpense: (expenseId: string, data: Partial<ExpenseDraft>, userId: string) => Promise<void>;
  updateExpenseStatus: (expenseId: string, status: ExpenseStatus) => Promise<void>;
  deleteExpense: (expenseId: string) => Promise<void>;
}

const expensesCollection = collection(db, 'expenses');

export const expenseRepository: ExpenseRepository = {
  async listExpenses() {
    const snap = await getDocs(query(expensesCollection, orderBy('transaction_date', 'desc')));
    return snap.docs.map((d) => mapExpenseDoc(d.id, d.data() as Record<string, unknown>));
  },

  async getExpenseById(expenseId) {
    const snap = await getDoc(doc(db, 'expenses', expenseId));
    return snap.exists() ? mapExpenseDoc(snap.id, snap.data() as Record<string, unknown>) : null;
  },

  async createExpense(data) {
    const docRef = await addDoc(expensesCollection, {
      ...data,
      created_at: new Date(),
      transaction_date: toTimestamp(data.transaction_date),
    });
    return docRef.id;
  },

  async updateExpense(expenseId, data, userId) {
    const payload: Record<string, unknown> = {
      ...data,
      last_updated_at: new Date(),
      updated_by: userId,
    };

    if (data.transaction_date) {
      payload.transaction_date = toTimestamp(data.transaction_date);
    }

    await updateDoc(doc(db, 'expenses', expenseId), payload);
  },

  async updateExpenseStatus(expenseId, status) {
    await updateDoc(doc(db, 'expenses', expenseId), { status });
  },

  async deleteExpense(expenseId) {
    await deleteDoc(doc(db, 'expenses', expenseId));
  },
};
